import React from "react";
import { FaStar, FaRegStar, FaStarHalfAlt } from "react-icons/fa";

interface StarRatingProps {
  rating: number;
  maxRating?: number;
  size?: "sm" | "md" | "lg";
  onChange?: (rating: number) => void;
  readonly?: boolean;
  showCount?: boolean;
}

const StarRating: React.FC<StarRatingProps> = ({
  rating,
  maxRating = 5,
  size = "md",
  onChange,
  readonly = false,
  showCount = false,
}) => {
  const [hoveredRating, setHoveredRating] = React.useState<number | null>(null);

  const sizeClasses = {
    sm: "text-sm",
    md: "text-lg",
    lg: "text-2xl",
  };

  const currentRating = hoveredRating ?? rating;

  const handleClick = (value: number) => {
    if (!readonly && onChange) {
      onChange(value);
    }
  };

  const handleMouseEnter = (value: number) => {
    if (!readonly && onChange) {
      setHoveredRating(value);
    }
  };

  const handleMouseLeave = () => {
    if (!readonly && onChange) {
      setHoveredRating(null);
    }
  };

  const stars = [];
  for (let i = 1; i <= maxRating; i++) {
    const filled = i <= Math.floor(currentRating);
    // Show half star if rating is between x.5 and x.9 (more standard approach)
    const halfFilled = !filled && i <= Math.ceil(currentRating) && currentRating % 1 >= 0.5;

    stars.push(
      <button
        key={i}
        type="button"
        onClick={() => handleClick(i)}
        onMouseEnter={() => handleMouseEnter(i)}
        onMouseLeave={handleMouseLeave}
        disabled={readonly}
        className={`${sizeClasses[size]} ${
          readonly ? "cursor-default" : "cursor-pointer hover:scale-110"
        } transition-all ${filled || halfFilled ? "text-yellow-400" : "text-gray-300"}`}
        aria-label={`Rate ${i} out of ${maxRating} stars`}
      >
        {filled ? (
          <FaStar className="inline-block" />
        ) : halfFilled ? (
          <FaStarHalfAlt className="inline-block" />
        ) : (
          <FaRegStar className="inline-block" />
        )}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {stars}
      {showCount && (
        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">({rating.toFixed(1)})</span>
      )}
    </div>
  );
};

export default StarRating;
